import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { UserRole } from '../../types/user';
import { profileService } from '../../services/profile';
import { useAuth } from '../../contexts/AuthContext';
import { getFullImageUrl } from '../../utils/imageUtils';
import { DEFAULT_AVATAR } from '../../utils/defaultAvatar';
import { InlineRoleSelection } from '../../components/profile/InlineRoleSelection';

const GUEST_SESSION_KEY = 'interviewr.guest.session';

function ResumeGuestDemoBanner() {
  const [info, setInfo] = useState<{ jobRole: string; type: string } | null>(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(GUEST_SESSION_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as {
        config?: { jobRole?: string; interviewType?: number };
      };
      const jobRole = parsed.config?.jobRole;
      if (!jobRole) return;
      const typeMap: Record<number, string> = {
        0: 'General',
        1: 'Behavioral',
        2: 'Technical',
        3: 'System Design',
      };
      setInfo({
        jobRole,
        type: typeMap[parsed.config?.interviewType ?? 0] ?? 'General',
      });
    } catch {
      /* ignore */
    }
  }, []);

  if (!info) return null;

  return (
    <div className="mb-6 flex flex-col items-start justify-between gap-3 rounded-stitch-xl border border-stitch-primary/30 bg-stitch-primary-soft px-4 py-3 sm:flex-row sm:items-center">
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-stitch-md bg-stitch-primary text-white">
          <span className="material-symbols-outlined text-lg">bookmark</span>
        </span>
        <div>
          <p className="text-sm font-semibold text-stitch-fg">Welcome back — your demo is saved</p>
          <p className="text-xs text-stitch-muted">
            {info.type} interview for {info.jobRole}. Pick up after profile setup.
          </p>
        </div>
      </div>
      <Link
        to="/try-interview"
        className="text-xs font-semibold text-stitch-primary hover:text-stitch-primary-hover"
      >
        View demo →
      </Link>
    </div>
  );
}

// Available languages for selection
const AVAILABLE_LANGUAGES = [
  'English', 'Georgian', 'Spanish', 'French', 'German', 'Chinese', 
  'Japanese', 'Russian', 'Arabic', 'Hindi', 'Portuguese', 'Italian', 'Korean'
];

// Available specialties for interviewers
const AVAILABLE_SPECIALTIES = [
  'Software Engineering', 'Frontend Development', 'Backend Development', 'Full Stack Development',
  'Mobile Development (iOS)', 'Mobile Development (Android)', 'DevOps Engineering',
  'Data Science', 'Machine Learning / AI', 'Cloud Architecture', 'Cybersecurity',
  'Product Management', 'Project Management', 'UX/UI Design', 'Quality Assurance',
  'Business Analysis', 'Marketing', 'Sales', 'Human Resources', 'Finance',
  'Customer Success', 'Other'
];

// Define a more flexible schema that can work with both user roles
const profileSchema = z.object({
  profileName: z.string().min(1, 'Profile name is required'),
  mobile: z.string().optional(),
  country: z.string().min(1, 'Country is required'),
  linkedinUrl: z.string().url('Invalid LinkedIn URL').optional().or(z.literal('')),
  specialty: z.string(),
  company: z.string(),
  experience: z.string()
    .max(1000, 'Experience must be 1000 characters or fewer'),
  languages: z.string(),
  role: z.enum(['interviewer', 'interviewee']),
  purpose: z.string().max(500, 'Purpose must be 500 characters or fewer').optional(),
  profilePictureUrl: z.string().optional(),
  // Make availableTimeSlots optional
  availableTimeSlots: z.array(z.object({
    startTime: z.date(),
    endTime: z.date(),
    isAvailable: z.boolean(),
  })).optional(),
}).superRefine((data, ctx) => {
  // Validate interviewer-specific required fields
  if (data.role === 'interviewer') {
    if (!data.specialty || data.specialty.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Specialty is required for interviewers",
        path: ["specialty"],
      });
    }
    if (!data.company || data.company.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Company is required for interviewers",
        path: ["company"],
      });
    }
    if (!data.experience || data.experience.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Experience is required for interviewers",
        path: ["experience"],
      });
    }
    if (!data.languages || data.languages.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "At least one language is required",
        path: ["languages"],
      });
    }
  }
  
  // Validate interviewee-specific required fields
  if (data.role === 'interviewee') {
    if (!data.purpose || data.purpose.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Purpose is required for interviewees",
        path: ["purpose"],
      });
    }
  }
});


type ProfileFormData = z.infer<typeof profileSchema>;

// Stitch tokens — work in light + dark
const inputStyles =
  'w-full rounded-stitch-lg border-2 border-stitch-border bg-stitch-surface px-4 py-3 text-stitch-fg shadow-sm transition-all placeholder:text-stitch-muted focus:border-stitch-primary focus:ring-2 focus:ring-stitch-primary/25';
const textareaStyles = `${inputStyles} resize-y`;

export function ProfileSetup() {
  const navigate = useNavigate();
  const { userRole, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profilePicture, setProfilePicture] = useState<File | null>(null);
  const [profilePictureError, setProfilePictureError] = useState<string | null>(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState<string | null>(null);
  const [existingProfilePictureUrl, setExistingProfilePictureUrl] = useState<string | null>(null);
  const [specialtySearch, setSpecialtySearch] = useState('');
  const [showSpecialtyDropdown, setShowSpecialtyDropdown] = useState(false);
  
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      role: 'interviewee',
      profileName: '',
      mobile: '',
      country: '',
      linkedinUrl: '',
      specialty: '',
      company: '',
      experience: '',
      languages: '',
      purpose: '',
    }
  });

  // Watch the role field and experience text for validation feedback
  const role = watch('role') as 'interviewer' | 'interviewee';
  const experienceValue = watch('experience') || '';
  const remainingExperienceChars = 1000 - experienceValue.length;
  const purposeValue = watch('purpose') || '';
  const remainingPurposeChars = 500 - purposeValue.length;

  useEffect(() => {
    // Set the role from auth context immediately
    if (userRole !== null) {
      const roleValue = userRole === UserRole.Interviewer ? 'interviewer' : 'interviewee';
      setValue('role', roleValue);
    }

    // Fetch existing profile data and pre-fill the form
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        const profile = await profileService.getProfile();
        if (profile) {
          // Pre-fill the form with existing data
          setValue('profileName', profile.profileName || '');
          setValue('mobile', profile.mobile || '');
          setValue('country', profile.country || '');
          setValue('linkedinUrl', profile.linkedinUrl || '');
          setValue('specialty', profile.specialty || '');
          setSpecialtySearch(profile.specialty || ''); // Also update the visible search input
          setValue('company', profile.company || '');
          setValue('experience', profile.experience || '');
          setValue('languages', (profile.languages && profile.languages.length > 0) ? profile.languages.join(', ') : '');
          
          // Load existing profile picture
          if (profile.profilePictureUrl) {
            setExistingProfilePictureUrl(getFullImageUrl(profile.profilePictureUrl) || null);
          }
          
          // Load purpose for both interviewers and interviewees
          setValue('purpose', profile.purpose || '');
          
          // Re-confirm the role after loading profile
          if (userRole !== null) {
            setValue('role', userRole === UserRole.Interviewer ? 'interviewer' : 'interviewee');
          }
        }
      } catch (err) {
        console.error("Failed to load profile:", err);
        // Don't set a generic error message, as it might confuse the user
        // setError('Failed to load your profile information.');
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, [setValue, userRole]);

  const onSubmit = async (data: ProfileFormData) => {
    try {
      setIsLoading(true);
      setError(null);

      // Validate ProfileName before submission
      if (!data.profileName || data.profileName.trim() === '') {
        setError('Profile Name is required. Please enter your profile name.');
        setIsLoading(false);
        return;
      }

      // Convert languages from comma-separated string to array
      const profileData = {
        // Remove role from profile data - role is managed separately during registration
        profileName: data.profileName.trim(),
        country: data.country,
        mobile: data.mobile || null,
        linkedinUrl: data.linkedinUrl || '',
        specialty: data.specialty,
        company: data.company || '',
        experience: data.experience.trim(),
        languages: data.languages.split(',').map(lang => lang.trim()).filter(lang => lang !== ''),
        purpose: data.purpose || null,
        profilePictureUrl: undefined // Will be set by backend if file is uploaded
      };

      console.log('📤 Submitting profile data:', profileData);

      // Call API to update profile with data and file in one request
      await profileService.updateProfile(profileData, profilePicture || undefined);
      
      navigate('/profile');
    } catch (err: any) {
      console.error('❌ Profile update error:', err);
      const errorMsg = err.response?.data?.message || err.response?.data?.details || err.message || 'Failed to save profile. Please try again.';
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProfilePictureError(null);
    setProfilePicturePreview(null);

    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      // Validation checks
      const maxSize = 20 * 1024 * 1024; // 20MB
      const allowedFormats = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];

      // Check file size
      if (file.size > maxSize) {
        setProfilePictureError(`File size must be less than 20MB. Your file is ${(file.size / 1024 / 1024).toFixed(2)}MB.`);
        return;
      }

      // Check file format
      if (!allowedFormats.includes(file.type)) {
        setProfilePictureError(`Invalid image format. Allowed formats: JPEG, PNG, WebP, GIF. Your file is ${file.type || 'unknown'}.`);
        return;
      }

      // Validate image dimensions (optional but good practice)
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          if (img.width < 100 || img.height < 100) {
            setProfilePictureError('Image must be at least 100x100 pixels.');
          } else {
            setProfilePicture(file);
            setProfilePicturePreview(event.target?.result as string);
          }
        };
        img.onerror = () => {
          setProfilePictureError('Unable to read image. Please try another file.');
        };
        img.src = event.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
  };

  if (authLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-stitch-primary border-t-transparent" />
      </div>
    );
  }

  if (userRole === null) {
    return <InlineRoleSelection />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stitch-primary-soft/40 via-stitch-canvas to-stitch-accent-soft/30 py-12 px-4 sm:px-6 lg:px-8 dark:from-stitch-canvas dark:via-stitch-canvas dark:to-stitch-canvas">
        <div className="mx-auto max-w-2xl">
          <div className="flex min-h-96 items-center justify-center rounded-stitch-2xl border border-stitch-border bg-stitch-surface p-8 shadow-stitch-card sm:p-10">
            <div className="text-center">
              <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-2 border-stitch-primary border-t-transparent" />
              <p className="text-stitch-muted">Loading your profile...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
      <div className="min-h-screen bg-gradient-to-br from-stitch-canvas via-stitch-surface to-stitch-primary-soft/40 py-12 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-2xl">
        <div className="rounded-stitch-2xl border border-stitch-border bg-stitch-surface p-8 shadow-stitch-pop sm:p-10">
          <div className="mb-8">
            <h2 className="mb-2 bg-gradient-to-r from-stitch-primary to-stitch-accent bg-clip-text text-3xl font-bold text-transparent">
              Complete Your Profile
            </h2>
            <p className="text-stitch-fg-soft">
              {role === 'interviewer'
                ? "✨ Set up your professional profile to start conducting interviews"
                : "✨ Help us personalize your interview experience"}
            </p>
          </div>
          <ResumeGuestDemoBanner />


        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Profile Picture Upload */}
          <div>
            <label className="mb-2 block text-sm font-bold text-stitch-fg">
              📸 Profile Picture
            </label>
            <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
              {/* Show existing or new preview */}
              <div className="flex-shrink-0">
                {profilePicturePreview ? (
                  <img 
                    src={profilePicturePreview} 
                    alt="New Preview" 
                    className="h-20 w-20 rounded-stitch-lg border-2 border-stitch-success object-cover shadow-md"
                    title="New image to be uploaded"
                  />
                ) : existingProfilePictureUrl ? (
                  <img 
                    src={existingProfilePictureUrl} 
                    alt="Current Profile" 
                    className="h-20 w-20 rounded-stitch-lg border-2 border-stitch-primary object-cover shadow-md"
                    onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                      (e.target as HTMLImageElement).src = DEFAULT_AVATAR;
                    }}
                    title="Current profile picture"
                  />
                ) : (
                  <div className="flex h-20 w-20 items-center justify-center rounded-stitch-lg border-2 border-stitch-border bg-stitch-surface-2 shadow-md">
                    <svg className="h-10 w-10 text-stitch-muted" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </div>
              
              <div className="w-full min-w-0 flex-1">
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                  onChange={handleFileChange}
                  className={`w-full max-w-full cursor-pointer rounded-stitch-lg border-2 border-dashed px-4 py-3 transition ${
                    profilePictureError
                      ? 'border-stitch-danger bg-stitch-danger-soft'
                      : 'border-stitch-border hover:border-stitch-primary hover:bg-stitch-primary-soft'
                  }`}
                />
                <p className="mt-2 text-xs text-stitch-muted">
                  ✓ Supported formats: JPEG, PNG, WebP, GIF (Max 20MB, minimum 100x100px)
                </p>
              </div>
            </div>
            {profilePictureError && (
              <p className="text-stitch-danger text-sm font-medium mt-2 flex items-center">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                {profilePictureError}
              </p>
            )}
          </div>

          {/* Common Fields */}
          <div>
            <label className="mb-2 block text-sm font-medium text-stitch-fg-soft">
              👤 Profile Name *
            </label>
            <input
              {...register('profileName')}
              type="text"
              placeholder="Your full name — e.g. Niko Turkishvili"
              className={`${inputStyles} ${errors.profileName ? 'border-stitch-danger/60 bg-stitch-danger-soft/40' : ''}`}
            />
            {errors.profileName ? (
              <p className="mt-1 text-sm text-stitch-danger">{errors.profileName.message}</p>
            ) : (
              <p className="mt-1 text-xs text-stitch-muted">
                Just your name — your role and profession are set separately below.
              </p>
            )}
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-stitch-fg-soft">
              📱 Mobile Number
            </label>
            <input
              {...register('mobile')}
              type="tel"
              placeholder="+1 234 567 8900"
              className={`${inputStyles} ${errors.mobile ? 'border-stitch-danger/60 bg-stitch-danger-soft/40' : ''}`}
            />
            {errors.mobile && (
              <p className="mt-1 text-sm text-stitch-danger">{errors.mobile.message}</p>
            )}
            <p className="mt-1 text-xs text-stitch-muted">Enter your mobile number with country code (e.g., +1 234 567 8900)</p>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-stitch-fg-soft">
               Country *
            </label>
            <input
              {...register('country')}
              type="text"
              placeholder="Enter your country"
              className={`${inputStyles} ${errors.country ? 'border-stitch-danger/60 bg-stitch-danger-soft/40' : ''}`}
            />
            {errors.country && (
              <p className="mt-1 text-sm text-stitch-danger">{errors.country.message}</p>
            )}
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-stitch-fg-soft">
              🔗 LinkedIn URL
            </label>
            <input
              {...register('linkedinUrl')}
              type="url"
              placeholder="https://linkedin.com/in/yourprofile"
              className={`${inputStyles} ${errors.linkedinUrl ? 'border-stitch-danger/60 bg-stitch-danger-soft/40' : ''}`}
            />
            {errors.linkedinUrl && (
              <p className="mt-1 text-sm text-stitch-danger">{errors.linkedinUrl.message}</p>
            )}
          </div>

          {/* Professional Information - Specialty with Search & Custom Option */}
          <div className="relative">
            <label className="mb-2 block text-sm font-medium text-stitch-fg-soft">
               Specialty * {role === 'interviewer' && <span className="text-xs text-stitch-primary">(required for interviewers)</span>}
            </label>
            <div className="relative">
              <input
                type="text"
                value={specialtySearch}
                onChange={(e) => {
                  setSpecialtySearch(e.target.value);
                  setValue('specialty', e.target.value);
                  setShowSpecialtyDropdown(true);
                }}
                onFocus={() => setShowSpecialtyDropdown(true)}
                onBlur={() => setTimeout(() => setShowSpecialtyDropdown(false), 200)}
                placeholder="Search or type to add custom specialty..."
                className={`${inputStyles} w-full ${errors.specialty ? 'border-stitch-danger/60 bg-stitch-danger-soft/40' : ''}`}
              />
              
              {showSpecialtyDropdown && (
                <div className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-stitch-lg border border-stitch-border bg-stitch-surface shadow-stitch-pop">
                  {/* Matching preset options */}
                  {AVAILABLE_SPECIALTIES.filter(spec => 
                    spec.toLowerCase().includes(specialtySearch.toLowerCase())
                  ).map((spec) => (
                    <div
                      key={spec}
                      onClick={() => {
                        setValue('specialty', spec);
                        setSpecialtySearch(spec);
                        setShowSpecialtyDropdown(false);
                      }}
                      className="cursor-pointer border-b border-stitch-border px-4 py-2 text-sm text-stitch-fg-soft transition last:border-b-0 hover:bg-stitch-surface-2"
                    >
                      {spec}
                    </div>
                  ))}
                  
                  {/* Custom option if text doesn't match any preset */}
                  {specialtySearch && !AVAILABLE_SPECIALTIES.some(spec => spec.toLowerCase() === specialtySearch.toLowerCase()) && (
                    <div
                      onClick={() => {
                        setValue('specialty', specialtySearch);
                        setShowSpecialtyDropdown(false);
                      }}
                      className="flex cursor-pointer items-center border-t-2 border-stitch-success/40 bg-stitch-success-soft px-4 py-3 text-sm font-semibold text-stitch-success transition hover:opacity-90"
                    >
                      <span className="mr-2 text-lg">➕</span>
                      Add custom specialty: <span className="ml-1 font-bold">"{specialtySearch}"</span>
                    </div>
                  )}
                  
                  {/* No results message */}
                  {!specialtySearch && AVAILABLE_SPECIALTIES.length === 0 && (
                    <div className="px-4 py-3 text-center text-sm text-stitch-muted">
                      No specialties available
                    </div>
                  )}
                </div>
              )}
              
              {/* Hidden field to store actual value - always synced with watch */}
              <input {...register('specialty')} type="hidden" value={watch('specialty') || ''} />
            </div>
            
            {/* Display selected specialty */}
            {watch('specialty') && (
              <div className="mt-2 inline-flex items-center rounded-full border border-stitch-primary/40 bg-stitch-primary-soft px-3 py-1">
                <span className="text-sm font-medium text-stitch-primary">✓ {watch('specialty')}</span>
                <button
                  type="button"
                  onClick={() => {
                    setValue('specialty', '');
                    setSpecialtySearch('');
                  }}
                  className="ml-2 font-bold text-stitch-primary hover:opacity-80"
                >
                  ✕
                </button>
              </div>
            )}
            
            {errors.specialty && (
              <p className="mt-2 text-sm text-stitch-danger font-medium flex items-center">
                <span className="mr-1">⚠</span>
                {errors.specialty.message}
              </p>
            )}
            <p className="mt-2 text-xs text-stitch-muted">💡 Tip: Search existing specialties or type a custom one (e.g., "Racing Instructor")</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-stitch-fg-soft">
              🏢 Company {role === 'interviewer' && <span className="text-xs text-stitch-primary">(required for interviewers)</span>}
            </label>
            <input
              {...register('company')}
              type="text"
              placeholder={role === 'interviewer' ? 'Enter your company name' : 'Optional'}
              className={`${inputStyles} ${errors.company ? 'border-stitch-danger/60 bg-stitch-danger-soft/40' : ''}`}
            />
            {errors.company && (
              <p className="mt-1 text-sm text-stitch-danger">{errors.company.message}</p>
            )}
          </div>

          <div>
            <label className="flex items-center justify-between text-sm font-medium text-stitch-fg-soft">
              <span>Experience</span>
              <span className={`text-xs ${remainingExperienceChars < 0 ? 'text-stitch-danger' : remainingExperienceChars <= 25 ? 'text-stitch-warning' : 'text-stitch-muted'}`}>
                {remainingExperienceChars} characters left
              </span>
            </label>
            <textarea
              {...register('experience')}
              rows={5}
              maxLength={1000}
              className={`${textareaStyles} ${errors.experience || remainingExperienceChars < 0 ? 'border-stitch-danger/60 bg-stitch-danger-soft/40' : ''}`}
              placeholder="Describe your professional experience (up to 1000 characters)"
            />
            {errors.experience && (
              <p className="mt-1 text-sm text-stitch-danger">{errors.experience.message}</p>
            )}
          </div>

          {/* Languages */}
          <div>
            <label className="mb-3 block text-sm font-medium text-stitch-fg-soft">
              Languages * (Select all that apply)
            </label>
            <div className={`grid grid-cols-2 gap-3 rounded-stitch-lg border-2 p-3 sm:grid-cols-3 ${errors.languages ? 'border-stitch-danger bg-stitch-danger-soft' : 'border-stitch-border'}`}>
              {AVAILABLE_LANGUAGES.map((lang) => (
                <label key={lang} className="flex cursor-pointer items-center space-x-2 rounded-stitch-md p-2 hover:bg-stitch-surface-2">
                  <input
                    type="checkbox"
                    value={lang}
                    onChange={(e) => {
                      const currentLangs = watch('languages') || '';
                      const langArray = currentLangs.split(',').map(l => l.trim()).filter(l => l);
                      if (e.target.checked) {
                        setValue('languages', [...langArray, lang].join(', '));
                      } else {
                        setValue('languages', langArray.filter(l => l !== lang).join(', '));
                      }
                    }}
                    checked={(watch('languages') || '').split(',').map(l => l.trim()).includes(lang)}
                    className="h-4 w-4 rounded border-stitch-border text-stitch-primary focus:ring-stitch-primary"
                  />
                  <span className="text-sm text-stitch-fg-soft">{lang}</span>
                </label>
              ))}
            </div>
            {errors.languages && (
              <p className="mt-1 text-sm text-stitch-danger">{errors.languages.message}</p>
            )}
          </div>

          {/* Role selection - hidden field that's set by default */}
          <input type="hidden" {...register('role')} />

          {/* Role-specific Fields */}
          {role === 'interviewer' && (
            <div>
              <label className="block text-sm font-medium text-stitch-fg-soft">
                Available Time Slots
              </label>
              {/* TODO: Implement time slot selection component */}
              <p className="mt-1 text-sm text-stitch-muted">
                Time slot selection will be implemented here
              </p>
            </div>
          )}

          {/* Purpose / About Section for both roles */}
          <div>
            <label className="mb-2 flex items-center justify-between text-sm font-medium text-stitch-fg-soft">
              <span>
                {role === 'interviewer' 
                  ? '📝 About Your Interview Program' 
                  : '🎯 Your Purpose'}
              </span>
              <span className={`text-xs ${remainingPurposeChars < 0 ? 'text-stitch-danger' : remainingPurposeChars <= 50 ? 'text-stitch-warning' : 'text-stitch-muted'}`}>
                {remainingPurposeChars} characters left
              </span>
            </label>
            <textarea
              {...register('purpose')}
              rows={6}
              maxLength={500}
              className={`${textareaStyles} ${remainingPurposeChars < 0 ? 'border-stitch-danger/60 bg-stitch-danger-soft/40' : ''}`}
              placeholder={
                role === 'interviewer'
                  ? "Describe your interview program, expertise areas, teaching approach, and what candidates can expect from sessions with you..."
                  : "What do you want to achieve with interviews? Share your goals, aspirations, and what you hope to gain from mock interviews..."
              }
            />
            {errors.purpose && (
              <p className="mt-1 text-sm text-stitch-danger">{errors.purpose.message}</p>
            )}
          </div>

          {error && (
            <div className="text-stitch-danger text-sm text-center">{error}</div>
          )}

          <div className="flex flex-col-reverse gap-3 pt-4 sm:flex-row">
            <button
              type="button"
              onClick={() => navigate('/profile')}
              className="flex-1 rounded-stitch-lg border border-stitch-border bg-stitch-surface-2 px-6 py-3 font-semibold text-stitch-fg transition hover:bg-stitch-border/30"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className={`flex flex-1 items-center justify-center gap-2 rounded-stitch-lg px-6 py-3 font-semibold text-white transition-all duration-200 ${
                isLoading 
                  ? 'cursor-not-allowed bg-stitch-muted' 
                  : 'bg-gradient-to-r from-stitch-primary to-stitch-accent shadow-stitch-card hover:opacity-95'
              }`}
            >
              {isLoading ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Saving...
                </>
              ) : (
                <>✓ Save Profile</>
              )}
            </button>
          </div>
        </form>
      </div>
      </div>
    </div>
  );
} 